import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  UserIcon,
  EnvelopeIcon,
  KeyIcon,
  PhotoIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentUser, updateUserProfile, uploadProfileImage } from '../services/api/auth';
import { TimezoneSelector } from './TimezoneSelector';

interface DBUserProfile {
  id: string;
  email: string;
  name: string;
  image?: string | null;
  emailVerified: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

interface FirebaseUserData {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  name: string | null;
  picture: string | null;
}

const AccountSettings: React.FC = () => {
  const { user, resetPassword, refreshProfile, resendVerificationEmail } = useAuth();
  const [dbProfile, setDbProfile] = useState<DBUserProfile | null>(null);
  const [firebaseData, setFirebaseData] = useState<FirebaseUserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [displayName, setDisplayName] = useState('');
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [timezone, setTimezone] = useState('UTC');

  // UI states
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Password reset states
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  // Email verification states
  const [sendingVerification, setSendingVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);

  // Account ID copy states
  const [copiedAccountId, setCopiedAccountId] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if user is using Google Sign-In
  const isGoogleUser = user?.providerData.some(provider => provider.providerId === 'google.com') || false;

  // Check if user is using email/password sign-in
  const isEmailPasswordUser = user?.providerData.some(provider => provider.providerId === 'password') || false;

  const getInitials = (name?: string | null) => {
    if (!name) return '';
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0]?.toUpperCase() ?? '')
      .join('');
  };

  // Load user profile from database
  useEffect(() => {
    async function loadProfile() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const idToken = await user.getIdToken();
        const response = await getCurrentUser(idToken);

        if (response.user) {
          setDbProfile(response.user);
          setDisplayName(response.user.name);
          setProfileImage(response.user.image || null);
          setTimezone(response.user.timezone || 'UTC');
        }

        if (response.firebase) {
          setFirebaseData(response.firebase);
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError(err instanceof Error ? err.message : 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [user]);

  // Handle image file selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSaveError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setSaveError('Image must be less than 5MB');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setSaveError(null);
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (!user || !dbProfile) return;

    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      const idToken = await user.getIdToken();
      const updates: { name?: string; image?: string } = {};
      let hasChanges = false;

      // Check if name changed
      if (displayName !== dbProfile.name) {
        updates.name = displayName;
        hasChanges = true;
      }

      // Upload image if selected
      if (imageFile && user) {
        const downloadURL = await uploadProfileImage(imageFile, idToken);
        updates.image = downloadURL;
        setProfileImage(downloadURL);
        setImagePreview(null);
        setImageFile(null);
        hasChanges = true;
      }

      // Update profile if there are profile changes
      if (Object.keys(updates).length > 0) {
        const updatedProfile = await updateUserProfile(idToken, updates);
        setDbProfile(updatedProfile);
      }

      // Save timezone if changed
      if (timezone !== dbProfile.timezone) {
        const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user-settings/timezone`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ timezone }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to save timezone');
        }

        hasChanges = true;
      }

      if (hasChanges) {
        // Refresh AuthContext to update navigation immediately
        await refreshProfile();

        setSaveSuccess(true);

        // Clear success message after 3 seconds
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError('No changes to save');
      }
    } catch (err) {
      console.error('Failed to save changes:', err);
      setSaveError(err instanceof Error ? err.message : 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // Handle password reset
  const handleChangePassword = async () => {
    if (!dbProfile?.email) return;

    try {
      setResettingPassword(true);
      setResetPasswordError(null);
      setResetPasswordSuccess(false);

      await resetPassword(dbProfile.email);

      setResetPasswordSuccess(true);

      // Clear success message after 5 seconds
      setTimeout(() => setResetPasswordSuccess(false), 5000);
    } catch (err) {
      console.error('Failed to send password reset email:', err);
      setResetPasswordError(err instanceof Error ? err.message : 'Failed to send password reset email');
    } finally {
      setResettingPassword(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    if (!user) return;

    try {
      setSendingVerification(true);
      setVerificationError(null);
      setVerificationSent(false);

      await resendVerificationEmail();

      setVerificationSent(true);

      // Clear success message after 5 seconds
      setTimeout(() => setVerificationSent(false), 5000);
    } catch (err: any) {
      console.error('Failed to send verification email:', err);
      setVerificationError(err.message || 'Failed to send verification email');

      // Clear error message after 5 seconds
      setTimeout(() => setVerificationError(null), 5000);
    } finally {
      setSendingVerification(false);
    }
  };

  // Handle copy account ID
  const handleCopyAccountId = async () => {
    if (!dbProfile?.id) return;

    try {
      await navigator.clipboard.writeText(dbProfile.id);
      setCopiedAccountId(true);

      // Clear the copied state after 2 seconds
      setTimeout(() => setCopiedAccountId(false), 2000);
    } catch (err) {
      console.error('Failed to copy account ID:', err);
    }
  };

  // Format date for display
  const formatDate = (date: Date | string | null) => {
    if (!date) return 'N/A';
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-300">Loading profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-red-400">Error: {error}</div>
      </div>
    );
  }

  if (!dbProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-300">No profile data available</div>
      </div>
    );
  }

  const resolvedName = displayName || dbProfile.name || firebaseData?.name || user?.displayName || user?.email || '';
  const profileInitials = getInitials(resolvedName) || '?';
  const profilePhoto = imagePreview || profileImage || firebaseData?.picture || null;

  return (
    <div className="space-y-8">
      {/* Success/Error Messages */}
      {saveSuccess && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg"
        >
          <CheckCircleIcon className="w-5 h-5 text-green-400" />
          <span className="text-green-300">Profile updated successfully!</span>
        </motion.div>
      )}

      {saveError && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
        >
          <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
          <span className="text-red-300">{saveError}</span>
        </motion.div>
      )}

      {/* Profile Information */}
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <UserIcon className="w-5 h-5" />
            Profile Information
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {/* Profile Image */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-3">
              Profile Image
            </label>
            <div className="flex items-center gap-6">
              <div className="relative">
                {profilePhoto ? (
                  <img
                    src={profilePhoto}
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover border-2 border-slate-600"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full border-2 border-slate-600 bg-gradient-to-r from-cyan-400 to-blue-500 flex items-center justify-center">
                    <span className="text-white font-semibold text-2xl">
                      {profileInitials}
                    </span>
                  </div>
                )}
                {imagePreview && (
                  <div className="absolute -top-2 -right-2 bg-blue-500 rounded-full p-1">
                    <CheckCircleIcon className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg flex items-center gap-2 transition-colors"
                >
                  <PhotoIcon className="w-5 h-5" />
                  Change Image
                </button>
                <p className="text-xs text-slate-400">
                  JPG, PNG or GIF. Max 5MB.
                </p>
              </div>
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-slate-300 mb-2">
              Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter your display name"
            />
          </div>

          {/* Email (Read-only) */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Email Address
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={dbProfile.email}
                readOnly
                className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-slate-400 cursor-not-allowed"
              />
              <EnvelopeIcon className="absolute right-3 top-2.5 w-5 h-5 text-slate-500" />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Email cannot be changed. Contact support if needed.
            </p>
          </div>

          {/* Timezone Preference */}
          <div className="pt-4 border-t border-slate-700">
            <h3 className="text-sm font-medium text-slate-300 mb-4 flex items-center gap-2">
              <GlobeAltIcon className="w-4 h-4 text-cyan-400" />
              Timezone Preference
            </h3>
            <p className="text-xs text-slate-400 mb-4">
              Your timezone affects when daily quotas reset and how usage statistics are calculated.
            </p>
            <TimezoneSelector
              value={timezone}
              onChange={setTimezone}
              disabled={saving}
            />
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <button
              onClick={handleSaveChanges}
              disabled={saving || (!imageFile && displayName === dbProfile.name && timezone === dbProfile.timezone)}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Password Section (Only for non-Google users) */}
      {!isGoogleUser && (
        <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-700">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <KeyIcon className="w-5 h-5" />
              Password
            </h2>
          </div>

          <div className="p-6 space-y-4">
            {/* Password Reset Success Message */}
            {resetPasswordSuccess && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/20 rounded-lg"
              >
                <CheckCircleIcon className="w-5 h-5 text-green-400" />
                <span className="text-green-300">
                  Password reset email sent! Check your inbox at {dbProfile?.email}
                </span>
              </motion.div>
            )}

            {/* Password Reset Error Message */}
            {resetPasswordError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
              >
                <ExclamationTriangleIcon className="w-5 h-5 text-red-400" />
                <span className="text-red-300">{resetPasswordError}</span>
              </motion.div>
            )}

            <div>
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={resettingPassword}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium rounded-lg transition-colors"
              >
                {resettingPassword ? 'Sending Email...' : 'Change Password'}
              </button>
              <p className="text-sm text-slate-400 mt-2">
                You'll receive an email with instructions to reset your password.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Google Account Notice */}
      {isGoogleUser && (
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
          <p className="text-blue-300 text-sm">
            You're signed in with Google. To manage your password, visit your{' '}
            <a
              href="https://myaccount.google.com/security"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-200"
            >
              Google Account settings
            </a>.
          </p>
        </div>
      )}

      {/* Account Information */}
      <div className="bg-slate-800/50 rounded-lg border border-slate-700 overflow-hidden">
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-xl font-semibold text-white">Account Information</h2>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex justify-between py-3 border-b border-slate-700">
            <span className="text-slate-400">Account Created</span>
            <span className="text-white">{formatDate(dbProfile.createdAt)}</span>
          </div>

          <div className="flex justify-between py-3 border-b border-slate-700">
            <span className="text-slate-400">Last Updated</span>
            <span className="text-white">{formatDate(dbProfile.updatedAt)}</span>
          </div>

          <div className="flex justify-between py-3 border-b border-slate-700">
            <span className="text-slate-400">Last Sign In</span>
            <span className="text-white">{formatDate(user?.metadata.lastSignInTime || null)}</span>
          </div>

          <div className="py-3 border-b border-slate-700">
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-400">Email Verified</span>
              <span className="text-white">
                {dbProfile.emailVerified ? (
                  <span className="flex items-center gap-1 text-green-400">
                    <CheckCircleIcon className="w-4 h-4" />
                    Verified
                  </span>
                ) : (
                  <span className="text-amber-400">Pending verification</span>
                )}
              </span>
            </div>

            {/* Resend Verification Button (only for unverified email/password users) */}
            {!dbProfile.emailVerified && isEmailPasswordUser && (
              <div className="mt-3 space-y-2">
                {/* Success Message */}
                {verificationSent && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg"
                  >
                    <CheckCircleIcon className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-sm text-green-300">Verification email sent! Check your inbox.</span>
                  </motion.div>
                )}

                {/* Error Message */}
                {verificationError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
                    <span className="text-sm text-red-300">{verificationError}</span>
                  </motion.div>
                )}

                {/* Resend Button */}
                {!verificationSent && (
                  <button
                    onClick={handleResendVerification}
                    disabled={sendingVerification}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    {sendingVerification ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                )}

                <p className="text-xs text-slate-400">
                  Check your inbox at <span className="font-medium text-slate-300">{dbProfile.email}</span>. Don't forget to check spam!
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center py-3">
            <span className="text-slate-400">Account ID</span>
            <button
              type="button"
              onClick={handleCopyAccountId}
              className="flex items-center gap-3 text-slate-500 text-sm font-mono transition-colors hover:text-slate-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500 rounded-md px-2 py-1"
              aria-live="polite"
            >
              <span className="truncate max-w-[320px] text-left">{dbProfile.id}</span>
              {copiedAccountId && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-300 bg-green-500/10 border border-green-500/20 rounded-full px-2 py-1">
                  <CheckCircleIcon className="w-4 h-4" />
                  Copied
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
